import { useEffect, useState } from 'react'
import type { JSX } from 'react'

import {
  PRESCRIPTION_STATUSES,
  RX_TYPES,
  dioptre,
  formatDioptre,
  type PrescriptionStatus,
  type RxType,
} from '@lensmira/domain'
import { formatDay } from '@lensmira/i18n'

import { ReadFailed, type PrescriptionCopyData } from '../api/reads.js'
import { Ltr, useTranslation } from '../i18n.js'
import { formatWireForDisplay } from '../money.js'
import { sentence } from '../sentence.js'
import { PrescriptionCopy } from '../screens/PrescriptionCopy.js'
import { orderStatusWord, rxStatusLabel, rxTypeLabel } from '../status-labels.js'
import type {
  PortalData,
  PortalEyeData,
  PortalOrderData,
  PortalOrderDetailData,
  PortalPrescriptionData,
} from './portal-reads.js'

/**
 * The customer's own page, reached from a link with no sign-in. §6.2 `CUS-007`.
 *
 * **Read only, and it says so by having nothing to press except "see the items".** There is no
 * write on this page, so `OFF-001a`'s queue does not apply, and the two loaders are injected, which
 * keeps `fetch` out of the component and lets a test hand it any answer.
 *
 * **It never explains why a link failed.** An expired link, a cancelled one and one that was made
 * up produce the same sentence from the server, and this page shows that sentence and nothing
 * more. The distinction is the staff's to see, not a stranger's.
 *
 * **Prescriptions are absent, not empty, when the link does not carry them**, and the page says
 * which. "None on file" and "not shown to you" are different statements, and the second one tells
 * the customer to ask the shop instead of concluding they have no prescription.
 *
 * Numbers are never mirrored, whatever the page direction (`DESIGN-TOKENS.md` section 6): money,
 * dates, invoice numbers and the dioptre grid all sit in `dir="ltr"`.
 */

export interface PortalProps {
  readonly load: () => Promise<PortalData>
  readonly loadOrder: (orderId: string) => Promise<PortalOrderDetailData>
  /** `CUS-007`: the customer's own downloadable copy of one prescription. */
  readonly loadCopy: (prescriptionId: string) => Promise<PrescriptionCopyData>
  /** The device's zone: a portal customer has no branch of their own to read a date against. */
  readonly timezone: string
}

type State =
  | { readonly kind: 'loading' }
  | { readonly kind: 'ready'; readonly data: PortalData }
  | { readonly kind: 'failed'; readonly status: number; readonly code: string }

const power = (value: number): string => {
  try {
    return formatDioptre(dioptre(value))
  } catch {
    // Data that crossed the network and is not a clean 0.25 step: show it as it is rather than
    // throwing on a page a customer is reading.
    return String(value)
  }
}

function EyeRow({
  label,
  eye,
}: {
  readonly label: string
  readonly eye: PortalEyeData
}): JSX.Element {
  return (
    <tr>
      <th scope="row">{label}</th>
      <td>{power(eye.sphere)}</td>
      <td>{power(eye.cylinder)}</td>
      <td>{eye.axis === undefined ? '' : eye.axis}</td>
      <td>{eye.add === undefined ? '' : power(eye.add)}</td>
    </tr>
  )
}

export function Portal({ load, loadOrder, loadCopy, timezone }: PortalProps): JSX.Element {
  const { t, locale } = useTranslation()
  const [state, setState] = useState<State>({ kind: 'loading' })
  const [open, setOpen] = useState<
    | { readonly kind: 'loading'; readonly id: string }
    | { readonly kind: 'ready'; readonly detail: PortalOrderDetailData }
    | { readonly kind: 'failed' }
    | null
  >(null)

  const [copy, setCopy] = useState<
    | { readonly kind: 'loading' }
    | { readonly kind: 'ready'; readonly copy: PrescriptionCopyData }
    | { readonly kind: 'failed' }
    | null
  >(null)

  const showCopy = (prescriptionId: string): void => {
    setCopy({ kind: 'loading' })
    loadCopy(prescriptionId).then(
      (loaded) => {
        setCopy({ kind: 'ready', copy: loaded })
      },
      () => {
        setCopy({ kind: 'failed' })
      },
    )
  }

  useEffect(() => {
    let live = true
    load().then(
      (data) => {
        if (live) setState({ kind: 'ready', data })
      },
      (error: unknown) => {
        if (!live) return
        setState(
          error instanceof ReadFailed
            ? { kind: 'failed', status: error.status, code: error.code }
            : { kind: 'failed', status: 0, code: 'UNREACHABLE' },
        )
      },
    )
    return () => {
      live = false
    }
  }, [load])

  const day = (value: string): string => formatDay(value, timezone, locale)

  if (copy?.kind === 'ready') {
    // The sheet is the one staff print (`RXW-003`); saving it as PDF from the print dialog is the
    // download, and it works on any phone without a second renderer.
    return (
      <main className="lm-screen">
        <div className="lm-screen__actions lm-no-print">
          <button
            type="button"
            className="lm-button lm-button--secondary"
            onClick={() => {
              setCopy(null)
            }}
          >
            {t('portal.rx.back')}
          </button>
        </div>
        <PrescriptionCopy copy={copy.copy} timezone={timezone} />
      </main>
    )
  }
  const money = (minor: string, currency: string): string => formatWireForDisplay(minor, currency)

  if (state.kind === 'loading') {
    return (
      <main className="lm-screen lm-screen--narrow">
        <h1 className="lm-screen__heading">{t('portal.title')}</h1>
        <p className="lm-hint" role="status">
          {t('portal.loading')}
        </p>
      </main>
    )
  }

  if (state.kind === 'failed') {
    // One sentence per kind of failure, and the kinds are the transport's, not the link's.
    const message =
      state.status === 404
        ? t('portal.unusable')
        : state.status === 429
          ? t('portal.tooMany')
          : state.status === 503
            ? t('portal.unavailable')
            : t('portal.failed')
    return (
      <main className="lm-screen lm-screen--narrow">
        <h1 className="lm-screen__heading">{t('portal.title')}</h1>
        <p className="lm-error" role="alert">
          {message}
        </p>
      </main>
    )
  }

  const { data } = state

  const showOrder = (id: string): void => {
    setOpen({ kind: 'loading', id })
    loadOrder(id).then(
      (detail) => {
        setOpen({ kind: 'ready', detail })
      },
      () => {
        setOpen({ kind: 'failed' })
      },
    )
  }

  const rxType = (type: string): string =>
    (RX_TYPES as readonly string[]).includes(type) ? rxTypeLabel(t, type as RxType) : type
  const rxStatus = (status: string): string =>
    (PRESCRIPTION_STATUSES as readonly string[]).includes(status)
      ? rxStatusLabel(t, status as PrescriptionStatus)
      : status

  const orderHeader = (order: PortalOrderData): JSX.Element => (
    <div className="lm-order-card__header">
      <span className="lm-order-card__number">
        {order.number === null ? t('portal.orders.noInvoice') : <Ltr>{order.number}</Ltr>}
      </span>
      <span className="lm-badge">{orderStatusWord(t, order.status)}</span>
    </div>
  )

  const amounts = (order: PortalOrderData): JSX.Element => (
    <>
      <p className="lm-order-card__customer">
        {sentence(t, 'portal.orders.placed', {
          date: <Ltr>{day(order.placedAt)}</Ltr>,
          branch: order.branchName,
        })}
      </p>
      <p className="lm-order-card__customer">
        {t('common.total')}: <Ltr>{money(order.total.minor, order.total.currency)}</Ltr>
      </p>
      <p className="lm-order-card__customer">
        {order.outstanding.minor === '0'
          ? t('portal.orders.paid')
          : sentence(t, 'portal.orders.owing', {
              amount: <Ltr>{money(order.outstanding.minor, order.outstanding.currency)}</Ltr>,
            })}
      </p>
    </>
  )

  if (open?.kind === 'ready') {
    const { order, lines } = open.detail
    return (
      <main className="lm-screen lm-screen--narrow">
        <h1 className="lm-screen__heading">{t('portal.title')}</h1>
        <button
          type="button"
          className="lm-button lm-button--secondary"
          onClick={() => {
            setOpen(null)
          }}
        >
          {t('portal.orders.back')}
        </button>
        <article className="lm-order-card">
          {orderHeader(order)}
          {amounts(order)}
          <table className="lm-line-table">
            <thead>
              <tr>
                <th scope="col">{t('portal.order.item')}</th>
                <th scope="col">{t('common.quantity')}</th>
                <th scope="col">{t('common.amount')}</th>
              </tr>
            </thead>
            <tbody>
              {lines.map((line, index) => (
                <tr key={index}>
                  <td>{line.description}</td>
                  <td>
                    <Ltr>{line.quantity}</Ltr>
                  </td>
                  <td>
                    <Ltr>{money(line.gross.minor, line.gross.currency)}</Ltr>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </article>
      </main>
    )
  }

  return (
    <main className="lm-screen lm-screen--narrow">
      <h1 className="lm-screen__heading">{t('portal.title')}</h1>
      <p>{t('portal.hello', { name: data.name })}</p>

      {open?.kind === 'failed' ? (
        <p className="lm-error" role="alert">
          {t('portal.failed')}
        </p>
      ) : null}

      <section aria-labelledby="portal-orders">
        <h2 id="portal-orders" className="lm-screen__subheading">
          {t('portal.orders.heading')}
        </h2>
        {data.orders.length === 0 ? (
          <p className="lm-hint">{t('portal.orders.none')}</p>
        ) : (
          <ul className="lm-order-list">
            {data.orders.map((order) => (
              <li key={order.id}>
                <article className="lm-order-card">
                  {orderHeader(order)}
                  {amounts(order)}
                  <button
                    type="button"
                    className="lm-button lm-button--secondary"
                    disabled={open?.kind === 'loading'}
                    onClick={() => {
                      showOrder(order.id)
                    }}
                  >
                    {t('portal.orders.see')}
                  </button>
                </article>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section aria-labelledby="portal-rx">
        <h2 id="portal-rx" className="lm-screen__subheading">
          {t('portal.rx.heading')}
        </h2>
        {copy?.kind === 'failed' ? (
          <p className="lm-hint" role="alert">
            {t('portal.rx.downloadFailed')}
          </p>
        ) : null}
        {data.prescriptions === undefined ? (
          <p className="lm-hint">{t('portal.rx.withheld')}</p>
        ) : data.prescriptions.length === 0 ? (
          <p className="lm-hint">{t('customerDetail.rx.none')}</p>
        ) : (
          <ul className="lm-rx-history">
            {data.prescriptions.map((rx: PortalPrescriptionData) => (
              <li key={rx.id}>
                <article className="lm-rx-version">
                  <div className="lm-screen__actions">
                    <span>{rxType(rx.type)}</span>
                    <span
                      className={rx.status === 'active' ? 'lm-badge lm-badge--open' : 'lm-badge'}
                    >
                      {rxStatus(rx.status)}
                    </span>
                  </div>
                  <table dir="ltr">
                    <thead>
                      <tr>
                        <th scope="col">{t('rx.col.eye')}</th>
                        <th scope="col">{t('rx.col.sphere')}</th>
                        <th scope="col">{t('rx.col.cylinder')}</th>
                        <th scope="col">{t('rx.col.axis')}</th>
                        <th scope="col">{t('rx.col.add')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      <EyeRow label={t('customerDetail.rx.row.right')} eye={rx.od} />
                      <EyeRow label={t('customerDetail.rx.row.left')} eye={rx.os} />
                    </tbody>
                  </table>
                  <button
                    type="button"
                    className="lm-button lm-button--secondary"
                    disabled={copy?.kind === 'loading'}
                    onClick={() => {
                      showCopy(rx.id)
                    }}
                  >
                    {t('portal.rx.download')}
                  </button>
                  <p className="lm-hint">
                    {rx.expiryDate === undefined
                      ? sentence(t, 'customerDetail.rx.issued', {
                          issued: <Ltr>{day(rx.issueDate)}</Ltr>,
                        })
                      : sentence(t, 'customerDetail.rx.issuedExpires', {
                          issued: <Ltr>{day(rx.issueDate)}</Ltr>,
                          expires: <Ltr>{day(rx.expiryDate)}</Ltr>,
                        })}
                  </p>
                </article>
              </li>
            ))}
          </ul>
        )}
      </section>
    </main>
  )
}
